// Vistas del Mapa (12-oct-2026; 25-oct-2026 quedan dos): una pregunta por vez.
//  - agenda:     el día: visitas, color del técnico y recorrido de cada uno
//  - pendientes: lo que queda por hacer: pedidos sin agendar y clientes con el service vencido o por
//                vencer, juntos, para ver qué queda cerca y llenar el día ("Armar día" lo arma solo).
//                Antes eran tres pestañas (Último service, Pendientes, Planificar) que repetían el Radar,
//                Trabajos → Por agendar y lo que ahora hace "Armar día".
// "¿Qué hay cerca?" no es una vista: es un foco con radio sobre cualquiera de ellas.
import { colorTecnico } from '../../utils/estados';
import { getTodayISO } from '../../utils/dateUtils';
import { claveAgenda } from '../../utils/ordenes';

export const VISTAS = [
    { id: 'agenda', label: 'Día' },
    { id: 'pendientes', label: 'Pendientes' },
    // 2-nov-2026: dónde están todos los equipos (también los que están al día, que no salían en ninguna vista)
    { id: 'equipos', label: 'Equipos' },
];
// Colores de Pendientes: pedidos en rojo/naranja, service en violeta (no se confunden con urgente)
// 26-oct-2026: reservado = la empresa ya le eligió día en el calendario de zonas (celeste)
export const COLOR = { urgente: '#991B1B', pedido: '#EA580C', reservado: '#0EA5E9', vencido: '#7C3AED', porVencer: '#A78BFA' };

// Misma regla que el Radar (20-oct-2026, backend ordenes/radar.py): limpieza cada 6 meses y filtro
// cada 12. Amarillo = vence en un mes o menos; rojo = ya venció.
export const SEMAFORO = [
    { id: 'verde', label: 'Al día (menos de 5 meses)', corto: 'Al día', color: '#16A34A' },
    { id: 'amarillo', label: 'Por vencer (5 meses, o 11 del filtro)', corto: 'Por vencer', color: '#D48800' },
    { id: 'rojo', label: 'Vencido (6+ meses, o filtro de un año)', corto: 'Vencido', color: '#DC2626' },
    { id: 'nunca', label: 'Sin service registrado', corto: 'Sin service', color: '#9CA3AF' },
];
const GRIS = '#B8B5AE';

// Meses entre una fecha yyyy-mm-dd y hoy (sin new Date sobre la fecha: en Argentina corre un día)
function mesesDesde(iso) {
    if (!iso) return null;
    const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    const [ha, hm, hd] = getTodayISO().split('-').map(Number);
    return (ha - a) * 12 + (hm - m) - (hd < d ? 1 : 0);
}

export function semaforo(lugar) {
    if (lugar.radar) return lugar.radar.estado === 'POR_VENCER' ? 'amarillo' : 'rojo';
    const m = mesesDesde(lugar.ultimaVisita);
    if (m == null) return 'nunca';
    // Lugares sin equipo con N/S (el Radar no los ve): misma regla con la fecha del último trabajo
    return m < 5 ? 'verde' : m < 6 ? 'amarillo' : 'rojo';
}

/** Nombre para mostrar: en los pedidos de empresa, el código y el lugar ("2041 · Raúl"), no el nombre de la
 * empresa (todos dirían "Modo Agua"). */
export function nombrePunto(p) {
    if (p?.pedidoId) return [p.codigo, p.lugar].filter(Boolean).join(' · ') || p.cliente || p.direccion || `Pedido #${p.pedidoId}`;
    return [p?.cliente, p?.sede && p.sede !== p.cliente ? p.sede : null].filter(Boolean).join(' · ') || p?.direccion || '';
}

export function kmEntre(a, b) {
    const r = 6371, rad = x => (x * Math.PI) / 180;
    const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
    return 2 * r * Math.asin(Math.sqrt(h));
}

/** ¿El punto pasa el filtro de cliente? filtro: 'todos' | 'EMPRESA' | 'PARTICULAR' | `c-<id>` */
export function pasaFiltro(p, filtro, tipos) {
    if (!filtro || filtro === 'todos') return true;
    if (filtro.startsWith('c-')) return String(p.clienteId) === filtro.slice(2);
    return (tipos?.[p.clienteId]?.tipo || 'PARTICULAR') === filtro;
}

export const HECHA = (v) => ['COMPLETADA', 'NO_ATENDIDO'].includes(v.estado);
export const COLOR_HECHA = '#78716C';

/** Puntos de la vista, con su color y etiqueta, ya filtrados por cliente (y por técnico en agenda). */
export function puntosDeVista(data, vista, { filtro, tecnico, capas = {}, extra = null, ocultos = {} }) {
    if (!data) return [];
    const tipos = data.tiposCliente;
    if (vista === 'equipos') {
        // Cada lugar con equipos, del color de su service; el número es cuántos equipos tiene
        return data.clientes
            .filter(c => (c.equipos > 0 || c.id === extra) && pasaFiltro(c, filtro, tipos))
            .map(c => ({ ...c, semaforo: semaforo(c) }))
            .filter(c => !ocultos[c.semaforo] || c.id === extra)
            .map(c => ({ ...c, capa: 'equipo', color: SEMAFORO.find(x => x.id === c.semaforo).color,
                etiqueta: c.equipos > 1 ? String(c.equipos) : null,
                // Vencidos y por vencer arriba de los verdes (antes quedaban tapados)
                z: c.semaforo === 'rojo' ? 600 : c.semaforo === 'amarillo' ? 400 : c.semaforo === 'nunca' ? 100 : 0 }));
    }
    if (vista === 'agenda') {
        // 28-oct-2026: el día como pasó y lo que falta. Primero lo hecho (por la hora en que se cerró, en gris con ✓);
        // después lo que falta, numerado 1, 2, 3… en el orden en que se va (como el Recorrido de hoy del Inicio)
        const porTec = {};
        const clave = (v) => (HECHA(v) ? `0${v.completada || ''}` : `1${claveAgenda(v.hora, v.ordenDia, v.ordenId)}`);
        return [...data.visitas]
            .sort((a, b) => clave(a).localeCompare(clave(b)))
            .map(v => {
                if (HECHA(v)) return { ...v, hecha: true, color: COLOR_HECHA, etiqueta: v.estado === 'COMPLETADA' ? '✓' : '✕' };
                porTec[v.tecnicoId] = (porTec[v.tecnicoId] || 0) + 1;
                return { ...v, color: colorTecnico(v.tecnico), etiqueta: String(porTec[v.tecnicoId]) };
            })
            .filter(v => (!tecnico || v.tecnicoId === tecnico) && pasaFiltro(v, filtro, tipos))
            // Lo que la empresa reservó para este día y todavía no se agendó (26-oct-2026): provisorio, en celeste
            .concat(tecnico ? [] : (data.pedidos || []).filter(p => p.rutaFecha && String(p.rutaFecha).slice(0, 10) === String(data.fecha).slice(0, 10)
                && pasaFiltro(p, filtro, tipos))
                .map(p => ({ ...p, capa: 'reservado', color: p.urgente ? COLOR.urgente : COLOR.reservado, etiqueta: null, hueco: true })));
    }
    const pedidos = capas.pedidos === false ? [] : data.pedidos.filter(p => pasaFiltro(p, filtro, tipos))
        .map(p => ({ ...p, capa: 'pedido', color: p.urgente ? COLOR.urgente : p.rutaFecha ? COLOR.reservado : COLOR.pedido, etiqueta: null, hueco: true }));
    const service = capas.service === false ? [] : data.clientes.filter(c => pasaFiltro(c, filtro, tipos))
        .map(c => ({ ...c, semaforo: semaforo(c) }))
        .filter(c => c.semaforo === 'rojo' || c.semaforo === 'amarillo' || c.id === extra)
        .map(c => ({ ...c, capa: 'service', color: c.semaforo === 'rojo' ? COLOR.vencido : c.semaforo === 'amarillo' ? COLOR.porVencer : SEMAFORO.find(x => x.id === c.semaforo).color,
            etiqueta: c.equipos > 1 ? String(c.equipos) : null }));
    // Lo que se buscó y no está en las capas (un cliente al día, o con la capa apagada): igual se muestra
    const extraP = extra && ![...pedidos, ...service].some(x => x.id === extra) ? data.clientes.find(c => c.id === extra) : null;
    const suelto = extraP ? [{ ...extraP, semaforo: semaforo(extraP), capa: 'service', color: SEMAFORO.find(x => x.id === semaforo(extraP)).color }] : [];
    return [...pedidos, ...service, ...suelto];
}

/** Recorridos de la agenda: una línea por técnico. Lo ya hecho, punteado en gris (por dónde pasó); lo que falta,
 *  en su color, desde la última que hizo (28-oct-2026). */
export function recorridosAgenda(puntos, salidaDe = null) {
    const por = {};
    puntos.filter(p => p.lat != null && p.capa !== 'reservado').forEach(p => { (por[p.tecnicoId] = por[p.tecnicoId] || []).push(p); });
    // 29-oct-2026: el recorrido arranca en el punto de salida de cada técnico (antes empezaba en el primer cliente)
    return Object.values(por).flatMap(ps => {
        const s = typeof salidaDe === 'function' ? salidaDe(ps[0]) : salidaDe;
        const inicio = s?.lat != null ? [s] : [];
        const hechas = ps.filter(p => p.hecha), faltan = ps.filter(p => !p.hecha);
        const color = faltan[0]?.color || colorTecnico(ps[0].tecnico);
        const desde = hechas.length ? [hechas[hechas.length - 1]] : inicio;
        return [
            { coords: [...(hechas.length ? inicio : []), ...hechas].map(p => [p.lat, p.lng]), color: COLOR_HECHA, punteada: true },
            { coords: [...desde, ...faltan].map(p => [p.lat, p.lng]), color },
        ];
    });
}

/** Leyenda de la vista con cantidades. */
export function leyenda(vista, puntos) {
    if (vista === 'equipos') {
        // 2-nov-2026: se cuentan LUGARES (los puntos que ves). Antes se sumaban los equipos de cada lugar: un lugar
        // vencido con 40 equipos sumaba 40 a "Vencido" aunque en el mapa fuera un solo punto
        return SEMAFORO.map(s => ({ id: s.id, label: s.corto, color: s.color,
            cantidad: puntos.filter(p => p.semaforo === s.id).length }));
    }
    if (vista === 'pendientes') {
        const ped = puntos.filter(p => p.capa === 'pedido'), ser = puntos.filter(p => p.capa === 'service');
        const urg = ped.filter(p => p.urgente).length, res = ped.filter(p => !p.urgente && p.rutaFecha).length;
        const venc = ser.filter(p => p.semaforo === 'rojo').length, por = ser.filter(p => p.semaforo === 'amarillo').length;
        return [{ id: 'urgente', label: 'Pedidos urgentes', color: COLOR.urgente, cantidad: urg, hueco: true },
            { id: 'reservado', label: 'Reservados por la empresa', color: COLOR.reservado, cantidad: res, hueco: true },
            { id: 'pedido', label: 'Pedidos sin día', color: COLOR.pedido, cantidad: ped.length - urg - res, hueco: true },
            { id: 'vencido', label: 'Service vencido', color: COLOR.vencido, cantidad: venc },
            { id: 'porVencer', label: 'Por vencer', color: COLOR.porVencer, cantidad: por }].filter(s => s.cantidad);
    }
    const tecs = {};
    const res = puntos.filter(p => p.capa === 'reservado');
    puntos = puntos.filter(p => p.capa !== 'reservado');
    puntos.forEach(p => { tecs[p.tecnicoId] = tecs[p.tecnicoId] || { id: p.tecnicoId, label: String(p.tecnico || '').split(' ')[0], color: colorTecnico(p.tecnico), cantidad: 0 }; tecs[p.tecnicoId].cantidad++; });
    return [...Object.values(tecs), ...(res.length ? [{ id: 'reservado', label: 'Reservado, falta agendar', color: COLOR.reservado, cantidad: res.length, hueco: true }] : [])];
}

/** Lo que hay cerca de un punto: pedidos sin agendar y lugares vencidos o sin service, por distancia. */
export function cercanos(data, centro, km, filtro) {
    if (!data || !centro || centro.lat == null) return [];
    const tipos = data.tiposCliente;
    const salida = [];
    data.pedidos.filter(p => p.lat != null && pasaFiltro(p, filtro, tipos)).forEach(p => {
        const d = kmEntre(centro, p);
        if (d <= km && p.id !== centro.id) salida.push({ ...p, tipoCerca: 'pedido', km: d });
    });
    data.clientes.filter(c => c.lat != null && pasaFiltro(c, filtro, tipos)).forEach(c => {
        const s = semaforo(c);
        if (s !== 'rojo' && s !== 'amarillo') return;
        const d = kmEntre(centro, c);
        if (d <= km && c.id !== centro.id) salida.push({ ...c, semaforo: s, tipoCerca: 'vender', km: d });
    });
    return salida.sort((a, b) => a.km - b.km);
}

/** Apaga (gris y chico) los puntos fuera del radio. */
export function conFoco(puntos, centro, km) {
    if (!centro) return puntos;
    return puntos.map(p => (p.id === centro.id || (p.lat != null && kmEntre(centro, p) <= km)
        ? p : { ...p, color: GRIS, etiqueta: null, apagado: true }));
}
